import { useEffect, useMemo, useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Search,
  RefreshCw,
  Inbox,
  ChevronLeft,
  ChevronRight,
  ChevronRight as OpenArrow,
  Filter,
  X,
  Wifi,
  WifiOff,
  CheckCircle2,
  Loader2,
  AlertOctagon,
  Layers,
  Clock,
  ExternalLink,
  Copy,
  Check,
  Trash2,
  RotateCw,
} from "lucide-react";

import { ocrApi } from "../../../services/ocrApi";
import { ExtractionStageChip } from "./ExtractionStageChip";
import { VerificationStatusBadges } from "./VerificationStatusBadges";
import { useOcrTrackingMessages } from "../../../hooks/useOcrTrackingMessages";
import {
  formatStatusLabel,
  parseSudhanDate,
  parseVerificationStatus,
  combinedStatusTone,
  type DocumentRecord,
  type DocumentRow,
  type OcrTrackingFrame,
  type TrackingPageRequest,
  type TrackingPageResponse,
} from "../../../types/documents";

interface SmartInboxProps {
  onOpenDocument?: (doc: DocumentRow) => void;
  pageSize?: number;
  /**
   * Which backend table feeds the inbox.
   *   "tracking"     → POST /tracking_page (default, doc_extraction_tracking)
   *   "configStudio" → POST /config_studio/homepage (config_studio_tracking)
   *
   * Mirrors the reference's two TrackingManager usages — OcrTrackingPage
   * stays on the default, TemplateEnginePage / Layout Config screen sets
   * "configStudio" so it shows docs uploaded for layout configuration.
   */
  dataSource?: "tracking" | "configStudio";
}

const DEFAULT_PAGE_SIZE = 25;

// ----------------------------------------------------------------- helpers

function formatTime(iso: string | null | undefined, fallback = "—"): string {
  if (!iso) return fallback;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return fallback;
  return d.toLocaleString();
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || "—";
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return d.toLocaleDateString();
}

interface FilterState {
  status: string;
  customer: string;
  layoutId: string;
  uploader: string;
  dateFrom: string;
  dateTo: string;
}

const EMPTY_FILTERS: FilterState = {
  status: "all",
  customer: "all",
  layoutId: "all",
  uploader: "all",
  dateFrom: "",
  dateTo: "",
};

function activeFilterCount(f: FilterState): number {
  let n = 0;
  if (f.status !== "all") n++;
  if (f.customer !== "all") n++;
  if (f.layoutId !== "all") n++;
  if (f.uploader !== "all") n++;
  if (f.dateFrom) n++;
  if (f.dateTo) n++;
  return n;
}

type PresetTone = "all" | "running" | "ok" | "fail";
const PRESETS: { id: PresetTone; label: string; Icon: typeof CheckCircle2; tone: string }[] = [
  { id: "all",     label: "All",         Icon: Inbox,         tone: "" },
  { id: "running", label: "In progress", Icon: Loader2,       tone: "text-amber-700 bg-amber-50 ring-amber-200" },
  { id: "ok",      label: "Completed",   Icon: CheckCircle2,  tone: "text-emerald-700 bg-emerald-50 ring-emerald-200" },
  { id: "fail",    label: "Failed",      Icon: AlertOctagon,  tone: "text-rose-700 bg-rose-50 ring-rose-200" },
];

// ----------------------------------------------------------------- main

export function SmartInbox({
  onOpenDocument,
  pageSize = DEFAULT_PAGE_SIZE,
  dataSource = "tracking",
}: SmartInboxProps) {
  const queryClient = useQueryClient();
  const [pageNumber, setPageNumber] = useState(1);
  const [query, setQuery] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS);
  const [preset, setPreset] = useState<PresetTone>("all");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [density, setDensity] = useState<"comfortable" | "compact">("comfortable");

  useEffect(() => {
    setPageNumber(1);
    setSelected(new Set());
  }, [
    query,
    filters.status,
    filters.customer,
    filters.layoutId,
    filters.uploader,
    filters.dateFrom,
    filters.dateTo,
    preset,
  ]);

  // Live row updates from the OCR ws-server. Mirrors
  // ocr-ui/src/shared/tracking/TrackingManager.jsx handleWSUpdate —
  //   - insert: prepend a fresh row to every cached tracking page.
  //   - update: merge `id_details` into the matching row (by process_id)
  //     across every cached page. Empty `id_details` payloads are no-ops.
  // The hook itself drops frames for other users and non-matching tables.
  //
  // The dataSource-scoped key prefix below keeps the two inbox variants
  // (tracking vs config-studio) from cross-contaminating each other's
  // cache. We also gate every frame on `table_name` so an insert/update
  // for the wrong table never mutates this inbox.
  const expectedTable =
    dataSource === "configStudio"
      ? "config_studio_tracking"
      : "doc_extraction_tracking";
  const cacheKeyPrefix = ["ocr", "tracking", dataSource] as const;
  const { connected: liveConnected } = useOcrTrackingMessages(
    (frame: OcrTrackingFrame) => {
      const id =
        frame.process_id ||
        frame.id_details?.process_id ||
        frame.execution_id;
      // eslint-disable-next-line no-console
      console.log("[SmartInbox WS]", {
        dataSource,
        expectedTable,
        action: frame.action,
        frame_table: frame.table_name,
        id,
        hasDetails: !!frame.id_details,
      });
      if (!id) return;
      // Skip frames for the other inbox's table. Missing table_name is
      // accepted for back-compat (older frames don't always carry it).
      if (frame.table_name && frame.table_name !== expectedTable) return;

      if (frame.action === "insert") {
        const details = frame.id_details;
        if (!details || Object.keys(details).length === 0) return;
        queryClient.setQueriesData<TrackingPageResponse>(
          { queryKey: cacheKeyPrefix, exact: false },
          (old) => {
            if (!old) return old;
            // Don't duplicate if the row is already cached on this page
            // (can happen when an upload triggers both an optimistic
            // insert and a server-side broadcast).
            if (old.rows.some((r) => r.process_id === id)) return old;
            const newRow = buildRowFromFrame(id, details);
            return {
              ...old,
              rows: [newRow, ...old.rows],
              totalRecords: (old.totalRecords ?? old.rows.length) + 1,
            };
          },
        );
        return;
      }

      if (
        frame.action === "update" ||
        frame.action === "process_complete"
      ) {
        const details = frame.id_details;
        if (!details || Object.keys(details).length === 0) return;
        queryClient.setQueriesData<TrackingPageResponse>(
          { queryKey: cacheKeyPrefix, exact: false },
          (old) => {
            if (!old?.rows) return old;
            let mutated = false;
            const rows = old.rows.map((row) => {
              if (row.process_id !== id) return row;
              mutated = true;
              return mergeRowUpdate(row, details);
            });
            return mutated ? { ...old, rows } : old;
          },
        );
      }
    },
    // Subscribe to the table this inbox actually displays. Without
    // mapping the dataSource → table_name, configStudio frames
    // (config_studio_tracking) are filtered out at the WS layer and
    // background-processing inserts never reach the cache, leaving the
    // inbox empty until the user manually refreshes.
    { tableName: expectedTable },
  );

  const optionsQuery = useQuery({
    queryKey: ["ocr", "tracking-options"],
    queryFn: () => ocrApi.trackingSearchOptions(),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });
  const options = optionsQuery.data;

  const trackingReq: TrackingPageRequest = useMemo(
    () => ({
      pageNumber,
      recordsCount: pageSize,
      search: query.trim() || undefined,
      status: filters.status,
      customer: filters.customer,
      layoutId: filters.layoutId,
      uploader: filters.uploader,
      dateFrom: filters.dateFrom || undefined,
      dateTo: filters.dateTo || undefined,
    }),
    [pageNumber, pageSize, query, filters],
  );

  // Pull rows from either /tracking_page or the config-studio pair
  // (/config_studio/homepage for unfiltered, /search for filtered). All
  // share the TrackingPageResponse shape so only the queryFn varies. The
  // dataSource is baked into the queryKey so the two inboxes (OCR
  // Tracking page vs Layout Config) keep separate cache entries.
  //
  // Matches the reference's split in tools/index.jsx — fetchData hits
  // /config_studio/homepage with just pagination, performSearch hits
  // /search with table_name="config_studio_tracking" + field/value when
  // the user types a query.
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["ocr", "tracking", dataSource, trackingReq],
    queryFn: () => {
      if (dataSource === "configStudio") {
        return trackingReq.search?.trim()
          ? ocrApi.configStudioSearch(trackingReq)
          : ocrApi.configStudioHomepage(trackingReq);
      }
      return ocrApi.trackingPage(trackingReq);
    },
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const errorMessage =
    error instanceof Error ? error.message : "Failed to load documents.";

  const rows = data?.rows ?? [];
  const total = data?.totalRecords ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const filterCount = activeFilterCount(filters);

  const filtered = useMemo<DocumentRow[]>(() => {
    let out = rows;
    if (preset !== "all") {
      // Use the combined tone (status + verification) so a doc whose
      // verification is fully green lands in "Completed" rather than
      // staying in "In progress" because its raw status string still
      // says e.g. BUSINESS_RULE_CONFIGURATION_COMPLETED.
      out = out.filter((d) => combinedStatusTone(d.status, d.verification) === preset);
    }
    if (query.trim()) {
      const q = query.toLowerCase();
      out = out.filter((d) =>
        [
          d.filename,
          d.process_id,
          d.execution_id,
          d.customer_name,
          d.layout_id,
          d.uploader,
          d.workflow_id,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q),
      );
    }
    return out;
  }, [rows, query, preset]);

  const presetCounts = useMemo(() => {
    const counts: Record<PresetTone, number> = { all: rows.length, running: 0, ok: 0, fail: 0 };
    for (const r of rows) {
      const t = combinedStatusTone(r.status, r.verification);
      if (t === "running") counts.running++;
      else if (t === "ok") counts.ok++;
      else if (t === "fail" || t === "warn") counts.fail++;
    }
    return counts;
  }, [rows]);

  const allSelected =
    filtered.length > 0 && filtered.every((d) => selected.has(d.process_id));
  const someSelected =
    !allSelected && filtered.some((d) => selected.has(d.process_id));

  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const d of filtered) next.delete(d.process_id);
      } else {
        for (const d of filtered) next.add(d.process_id);
      }
      return next;
    });
  };

  const toggleOne = (pid: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(pid)) next.delete(pid);
      else next.add(pid);
      return next;
    });
  };

  const clearFilters = () => {
    setFilters(EMPTY_FILTERS);
    setQuery("");
    setPreset("all");
  };

  const clearSelection = () => setSelected(new Set());

  // ----- Bulk delete -------------------------------------------------------
  // POST /document_extraction/tracking_page/delete
  // Body: { process_id: [...], table_name: "doc_extraction_tracking" }
  // Optimistically removes the deleted rows from every cached tracking page
  // so the table doesn't flash an empty/loading state during the refetch.
  const deleteMutation = useMutation({
    // `tableName` mirrors `expectedTable` so configStudio deletes hit
    // `config_studio_tracking` instead of `doc_extraction_tracking` —
    // without it the backend can't find the row and the optimistic
    // remove rolls back.
    mutationFn: (ids: string[]) =>
      ocrApi.deleteTrackingDocuments(ids, { tableName: expectedTable }),
    onMutate: (ids: string[]) => {
      const idSet = new Set(ids);
      const snapshots = queryClient.getQueriesData<TrackingPageResponse>({
        queryKey: cacheKeyPrefix,
        exact: false,
      });
      queryClient.setQueriesData<TrackingPageResponse>(
        { queryKey: cacheKeyPrefix, exact: false },
        (old) => {
          if (!old?.rows) return old;
          const rows = old.rows.filter((r) => !idSet.has(r.process_id));
          if (rows.length === old.rows.length) return old;
          return {
            ...old,
            rows,
            totalRecords: Math.max(0, (old.totalRecords ?? rows.length) - (old.rows.length - rows.length)),
          };
        },
      );
      return { snapshots };
    },
    onError: (err, _ids, ctx) => {
      // Roll back the optimistic update if the request failed.
      if (ctx?.snapshots) {
        for (const [key, data] of ctx.snapshots) {
          queryClient.setQueryData(key, data);
        }
      }
      const message =
        err instanceof Error ? err.message : "Couldn't delete the selected documents.";
      toast.error("Delete failed", { description: message });
    },
    onSuccess: (_data, ids) => {
      toast.success(
        ids.length === 1
          ? "Document deleted"
          : `${ids.length} documents deleted`,
      );
      setSelected(new Set());
    },
    onSettled: () => {
      // Re-sync against the server so any partial-success / server-side
      // bookkeeping (e.g. counts, recently-uploaded ordering) is correct.
      queryClient.invalidateQueries({ queryKey: cacheKeyPrefix, exact: false });
      queryClient.invalidateQueries({ queryKey: ["ocr", "dashboard"], exact: false });
    },
  });

  const handleDeleteSelected = () => {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    const ok = window.confirm(
      ids.length === 1
        ? "Delete this document? This can't be undone."
        : `Delete ${ids.length} documents? This can't be undone.`,
    );
    if (!ok) return;
    deleteMutation.mutate(ids);
  };

  const copyId = async (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId((current) => (current === id ? null : current)), 1200);
    } catch {
      /* clipboard may be blocked; ignore silently */
    }
  };

  const rowPad = density === "compact" ? "py-1.5" : "py-3";

  return (
    <section className="rounded-xl border border-gray-200 bg-white min-w-0 overflow-hidden shadow-sm">
      {/* === Header (subtle gradient for visual lift) ==================== */}
      <header className="flex flex-wrap items-center gap-3 border-b border-gray-100 bg-gradient-to-r from-gray-50/80 via-white to-white px-5 py-3">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-blue-50">
            <Inbox className="h-4 w-4 text-blue-600" />
          </span>
          <h2 className="text-sm font-semibold text-gray-900">Smart inbox</h2>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600 tabular-nums">
            {total.toLocaleString()}
          </span>
        </div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search filename, vendor, layout, process id…"
              className="w-72 rounded-md border border-gray-300 bg-white py-1 pl-7 pr-2 text-xs text-gray-900 placeholder:text-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>

          <button
            type="button"
            onClick={() => setFiltersOpen((v) => !v)}
            className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
              filtersOpen || filterCount > 0
                ? "border-blue-300 bg-blue-50 text-blue-700"
                : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
            {filterCount > 0 ? (
              <span className="ml-1 rounded-full bg-blue-600 px-1.5 text-[10px] font-semibold text-white tabular-nums">
                {filterCount}
              </span>
            ) : null}
          </button>

          <button
            type="button"
            onClick={() => setDensity((d) => (d === "compact" ? "comfortable" : "compact"))}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
            title={density === "compact" ? "Switch to comfortable rows" : "Switch to compact rows"}
          >
            <Layers className="h-3.5 w-3.5" />
            {density === "compact" ? "Compact" : "Comfortable"}
          </button>

          <button
            type="button"
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </button>

          <span
            title={
              liveConnected
                ? "Connected to ws-server -- rows update automatically as the pipeline progresses"
                : "ws-server offline -- rows refresh only on Refresh"
            }
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${
              liveConnected
                ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
                : "bg-gray-100 text-gray-500 ring-1 ring-gray-200"
            }`}
          >
            {liveConnected ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
            {liveConnected ? "Live" : "Offline"}
          </span>
        </div>
      </header>

      {/* === Preset filter chips ========================================= */}
      <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-2">
        {PRESETS.map((p) => {
          const active = preset === p.id;
          const count = presetCounts[p.id];
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setPreset(p.id)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ring-1 transition ${
                active
                  ? p.tone || "bg-blue-50 text-blue-700 ring-blue-200"
                  : "bg-white text-gray-600 ring-gray-200 hover:bg-gray-50 hover:text-gray-900"
              }`}
            >
              <p.Icon className={`h-3.5 w-3.5 ${active && p.id === "running" ? "animate-spin" : ""}`} />
              {p.label}
              <span className={`rounded-full px-1.5 text-[10px] tabular-nums ${active ? "bg-white/70" : "bg-gray-100 text-gray-600"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* === Advanced filters ============================================ */}
      {filtersOpen ? (
        <div className="grid gap-3 border-b border-gray-100 bg-gray-50/60 px-5 py-3 sm:grid-cols-2 lg:grid-cols-6">
          <FilterSelect
            label="Status"
            value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={options?.statuses ?? []}
            formatLabel={formatStatusLabel}
          />
          <FilterSelect
            label="Customer"
            value={filters.customer}
            onChange={(v) => setFilters((f) => ({ ...f, customer: v }))}
            options={options?.customers ?? []}
          />
          <FilterSelect
            label="Layout"
            value={filters.layoutId}
            onChange={(v) => setFilters((f) => ({ ...f, layoutId: v }))}
            options={options?.layouts ?? []}
          />
          <FilterSelect
            label="Uploader"
            value={filters.uploader}
            onChange={(v) => setFilters((f) => ({ ...f, uploader: v }))}
            options={options?.uploaders ?? []}
          />
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-gray-600">From</span>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium text-gray-600">To</span>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
              className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </label>

          {filterCount > 0 || query || preset !== "all" ? (
            <div className="sm:col-span-2 lg:col-span-6 flex justify-end">
              <button
                type="button"
                onClick={clearFilters}
                className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
              >
                <X className="h-3.5 w-3.5" />
                Clear all
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* === Bulk action toolbar ========================================= */}
      {selected.size > 0 ? (
        <div className="flex items-center gap-3 border-b border-blue-200 bg-blue-50 px-5 py-2 text-xs">
          <span className="font-semibold text-blue-700 tabular-nums">
            {selected.size} selected
          </span>
          <span className="text-blue-300">·</span>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-white px-2 py-1 text-blue-700 hover:bg-blue-100"
          >
            <RotateCw className="h-3.5 w-3.5" />
            Reverify
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-md border border-blue-200 bg-white px-2 py-1 text-blue-700 hover:bg-blue-100"
          >
            <ExternalLink className="h-3.5 w-3.5" />
            Export
          </button>
          <button
            type="button"
            onClick={handleDeleteSelected}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-rose-700 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {deleteMutation.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Trash2 className="h-3.5 w-3.5" />
            )}
            {deleteMutation.isPending ? "Deleting…" : "Delete"}
          </button>
          <button
            type="button"
            onClick={clearSelection}
            className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-1 text-blue-700 hover:bg-blue-100"
          >
            <X className="h-3.5 w-3.5" />
            Clear
          </button>
        </div>
      ) : null}

      {/* === Table / states ============================================== */}
      {isLoading ? (
        <div className="px-5 py-10 text-center text-sm text-gray-500">Loading…</div>
      ) : isError ? (
        <div className="px-5 py-8 text-center text-sm">
          <div className="mx-auto max-w-md rounded-md border border-rose-200 bg-rose-50 p-4 text-left text-rose-700">
            <p className="font-medium">Couldn't load documents.</p>
            <p className="mt-1 text-rose-600">{errorMessage}</p>
            <button
              type="button"
              className="mt-3 inline-flex items-center rounded-md border border-rose-300 bg-white px-2 py-1 text-xs font-medium text-rose-700 hover:bg-rose-100"
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center px-5 py-12 text-center">
          <div className="mb-3 inline-flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400">
            <Inbox className="h-5 w-5" />
          </div>
          <p className="text-sm font-medium text-gray-700">
            {query || filterCount > 0 || preset !== "all"
              ? "No documents match your filters."
              : "Inbox is empty."}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {query || filterCount > 0 || preset !== "all"
              ? "Try clearing a filter or searching differently."
              : "Drop a file in the dropzone above to get started."}
          </p>
          {(query || filterCount > 0 || preset !== "all") ? (
            <button
              type="button"
              onClick={clearFilters}
              className="mt-3 inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
            >
              <X className="h-3.5 w-3.5" />
              Clear filters
            </button>
          ) : null}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[960px] text-sm">
            <thead className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500 shadow-[inset_0_-1px_0_rgb(229_231_235)]">
              <tr>
                <th className="w-10 px-3 py-2.5">
                  <input
                    type="checkbox"
                    aria-label="Select all on page"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={toggleAll}
                    className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
                  />
                </th>
                <th className="px-5 py-2.5">Document</th>
                <th className="px-5 py-2.5">Layout / Workflow</th>
                <th className="whitespace-nowrap px-3 py-2.5">Status</th>
                <th className="px-5 py-2.5">Verification</th>
                <th className="px-5 py-2.5">Started</th>
                <th className="px-5 py-2.5">Uploader</th>
                <th className="w-10 px-3 py-2.5" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((doc) => {
                const isSel = selected.has(doc.process_id);
                const shortId = doc.process_id.slice(0, 8);

                return (
                  <tr
                    key={doc.process_id}
                    className={`group cursor-pointer transition-colors ${
                      isSel ? "bg-blue-50/70" : "hover:bg-blue-50/40"
                    }`}
                    onClick={(e) => {
                      const target = e.target as HTMLElement;
                      if (target.closest("[data-row-checkbox]")) return;
                      onOpenDocument?.(doc);
                    }}
                  >
                    <td className={`w-10 px-3 ${rowPad}`} data-row-checkbox>
                      <input
                        type="checkbox"
                        aria-label={`Select ${doc.filename}`}
                        checked={isSel}
                        onChange={() => toggleOne(doc.process_id)}
                        onClick={(e) => e.stopPropagation()}
                        className="h-3.5 w-3.5 cursor-pointer accent-blue-600"
                      />
                    </td>

                    <td className={`px-5 ${rowPad}`}>
                      <div className="flex flex-col gap-0.5">
                        <div className="font-medium text-gray-900 truncate max-w-[16rem]" title={doc.filename}>
                          {doc.filename}
                        </div>
                        <div className="flex items-center gap-1 text-[11px] text-gray-400">
                          <span className="font-mono" title={doc.process_id}>
                            {shortId}…
                          </span>
                          <button
                            type="button"
                            onClick={(e) => copyId(e, doc.process_id)}
                            className="inline-flex h-4 w-4 items-center justify-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-700 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition"
                            aria-label="Copy process id"
                            title="Copy process id"
                          >
                            {copiedId === doc.process_id ? (
                              <Check className="h-3 w-3 text-emerald-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </button>
                        </div>
                      </div>
                    </td>

                    <td className={`px-5 ${rowPad}`}>
                      <div className="flex flex-col gap-0.5 text-xs">
                        <span className="text-gray-700 truncate max-w-[12rem]">
                          {doc.layout_id || "—"}
                        </span>
                        {doc.workflow_id ? (
                          <span className="font-mono text-[11px] text-gray-400 truncate max-w-[12rem]" title={doc.workflow_id}>
                            {doc.workflow_id}
                          </span>
                        ) : null}
                      </div>
                    </td>

                    <td className={`px-3 ${rowPad}`}>
                      <ExtractionStageChip
                        status={doc.status}
                        verification={doc.verification}
                      />
                    </td>

                    <td className={`px-5 ${rowPad}`}>
                      {/* `item` omitted intentionally — badges in the inbox
                          should not navigate. Row click on the table cell
                          already opens the document panel; the badges here
                          are read-only signals. */}
                      <VerificationStatusBadges status={doc.verification} />
                    </td>

                    <td className={`px-5 ${rowPad} whitespace-nowrap`}>
                      <span
                        className="inline-flex items-center gap-1 text-gray-600"
                        title={formatTime(doc.startedAtIso, doc.start_time || "—")}
                      >
                        <Clock className="h-3 w-3 text-gray-400" />
                        {formatRelative(doc.startedAtIso || doc.start_time)}
                      </span>
                    </td>

                    <td className={`px-5 ${rowPad} text-gray-700 truncate max-w-[10rem]`}>
                      {doc.uploader || "—"}
                    </td>

                    <td className={`w-10 px-3 ${rowPad} text-right`}>
                      <OpenArrow className="ml-auto h-4 w-4 text-gray-300 transition-transform group-hover:text-blue-500 group-hover:translate-x-0.5" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {total > pageSize ? (
        <footer className="flex items-center justify-between border-t border-gray-100 px-5 py-2 text-xs text-gray-600">
          <div>
            Page <span className="font-medium text-gray-900">{pageNumber}</span> of{" "}
            <span className="tabular-nums">{totalPages}</span>{" "}
            <span className="text-gray-400">({total.toLocaleString()} records)</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              disabled={pageNumber <= 1 || isFetching}
              onClick={() => setPageNumber((n) => Math.max(1, n - 1))}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 hover:bg-gray-50 disabled:opacity-50"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Prev
            </button>
            <button
              type="button"
              disabled={pageNumber >= totalPages || isFetching}
              onClick={() => setPageNumber((n) => Math.min(totalPages, n + 1))}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </footer>
      ) : null}
    </section>
  );
}

// ----------------------------------------------------------------- helpers

type FrameDetails = NonNullable<OcrTrackingFrame["id_details"]>;

/**
 * Construct a fresh DocumentRow from an insert frame's `id_details`.
 * Mirrors `enrichRow` in services/ocrApi.ts — we need the same parsed
 * shape (verification map, ISO start time) so the row renders correctly
 * before the next /tracking_page refetch overwrites it.
 */
function buildRowFromFrame(id: string, details: FrameDetails): DocumentRow {
  const raw = details as Partial<DocumentRecord>;
  const record: DocumentRecord = {
    id: typeof raw.id === "number" ? raw.id : 0,
    execution_id: raw.execution_id ?? "",
    process_id: id,
    customer_name: raw.customer_name ?? "",
    service: raw.service ?? "",
    layout_id: raw.layout_id ?? "",
    workflow_id: raw.workflow_id ?? "",
    start_time: raw.start_time ?? "",
    duration: raw.duration ?? "",
    uploader: raw.uploader ?? "",
    verification_status: raw.verification_status ?? null,
    status: raw.status ?? "",
    filename: raw.filename ?? "",
  };
  return {
    ...record,
    verification: parseVerificationStatus(record.verification_status),
    startedAtIso: parseSudhanDate(record.start_time),
  };
}

/**
 * Merge an update frame's `id_details` into an existing row. Re-derives
 * the parsed verification map and ISO start time only when their source
 * fields are actually present in the update.
 */
function mergeRowUpdate(prev: DocumentRow, details: FrameDetails): DocumentRow {
  // Strip the user_id / service helper fields so they don't pollute the
  // row — `DocumentRecord` doesn't have these. Anything else we let
  // through and overwrite the prior value with.
  const { user_id: _ignoredUser, service: _ignoredService, ...rest } = details;
  void _ignoredUser;
  void _ignoredService;
  const next: DocumentRow = { ...prev, ...(rest as Partial<DocumentRecord>) };
  if (Object.prototype.hasOwnProperty.call(details, "verification_status")) {
    next.verification = parseVerificationStatus(next.verification_status);
  }
  if (Object.prototype.hasOwnProperty.call(details, "start_time")) {
    next.startedAtIso = parseSudhanDate(next.start_time);
  }
  return next;
}

interface FilterSelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: string[];
  formatLabel?: (v: string) => string;
}

function FilterSelect({
  label,
  value,
  onChange,
  options,
  formatLabel,
}: FilterSelectProps) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="font-medium text-gray-600">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        <option value="all">All</option>
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {formatLabel ? formatLabel(opt) : opt}
          </option>
        ))}
      </select>
    </label>
  );
}
